import { compact } from 'lodash-es';
import moment from 'moment';
import { a as BxfDatePickerDateFormatEnum, B as BxfCalendarConstants } from './bxf-date-picker-separators.enum-a0b9baeb.js';
import { a as querySelectorDeep } from './query-selector-shadow-dom-dc946919.js';
import { B as BxfOverlayDropdownController } from './overlay-dropdown-controller-d9d1cfbe.js';

const { TABLE_CELL_TODAY, TABLE_CELL_SELECTED, TABLE_CELL_DISABLED, FULL_YEAR, TABLE_CELL_RANGE_BOUND, SUNDAY_RANGE, } = BxfCalendarConstants.STRING;
class BxfCalendarCell {
    _currentMoment;
    _element;
    _isSelected = false;
    _dateFormat = BxfDatePickerDateFormatEnum.DD_MM_YYYY;
    _inRange = false;
    _isTodayHighlighted;
    isToday = false;
    isDisabled = false;
    isHoliday = false;
    isDateTypeA = false;
    isDateTypeB = false;
    holidayPopover;
    markerAPopover;
    markerBPopover;
    popoverElement;
    get element() {
        return this._element;
    }
    set element(value) {
        this._element = value;
        if (value) {
            this.setSelected();
        }
    }
    get isSelected() {
        return this._isSelected;
    }
    set isSelected(value) {
        this._isSelected = value;
        this.setSelected();
    }
    get dateMoment() {
        return this._currentMoment;
    }
    dateMatch(events, dateFormat, separator) {
        return events?.map((dates) => {
            if (dates) {
                const values = dates[this._currentMoment.format(FULL_YEAR)] ?? [];
                const matches = values.length && typeof values[0] !== 'string'
                    ? values.filter((holiday) => holiday.value ===
                        this._currentMoment.format(dateFormat.replace(/\s/g, separator)))
                    : [
                        this.convertToHolidayObject(this._currentMoment.format(dateFormat.replace(/\s/g, separator)), values),
                    ];
                return {
                    isEvent: compact(matches)?.length > 0,
                    popoverInfo: matches[0]?.popover,
                };
            }
            return { isEvent: false, popoverInfo: null };
        });
    }
    convertToHolidayObject(dateMoment, values) {
        const formattedDate = dateMoment;
        return values?.includes(formattedDate)
            ? { value: formattedDate }
            : null;
    }
    constructor(currentMoment, dateFormat, isTodayHighlighted) {
        this._currentMoment = currentMoment;
        this._dateFormat = dateFormat;
        this._isTodayHighlighted = isTodayHighlighted;
        this.setIsToday();
    }
    setIsToday() {
        const todayMoment = moment(moment().format(this._dateFormat), this._dateFormat);
        this.isToday = this._currentMoment.isSame(todayMoment);
    }
    setMarkerData(events, dateFormat, separator) {
        // fallback to this._dateFormat on close/destroy
        const data = this.dateMatch(events, dateFormat ?? this._dateFormat, separator);
        this.isHoliday = data[0].isEvent;
        this.holidayPopover = data[0].popoverInfo;
        this.isDateTypeA = data[1].isEvent;
        this.markerAPopover = data[1].popoverInfo;
        this.isDateTypeB = data[2].isEvent;
        this.markerBPopover = data[2].popoverInfo;
    }
    isSame(value = moment(moment().format(this._dateFormat), this._dateFormat)) {
        return this._currentMoment.isSame(value);
    }
    isSunday() {
        const dayOfWeek = this._currentMoment.weekday();
        return (BxfCalendarConstants.STRING.SUNDAY_FULL ===
            BxfCalendarConstants.FULL_DAYS[dayOfWeek]);
    }
    getMomentAsString() {
        return this._currentMoment.format(this._dateFormat);
    }
    isWithinRange(start, end) {
        if (!start)
            return false;
        this._inRange =
            this._currentMoment.isAfter(start) &&
                this._currentMoment.isSameOrBefore(end);
        return this._inRange && !this.isSunday();
    }
    hasPopoverInfo() {
        return (!!this.holidayPopover ||
            !!this.markerAPopover ||
            !!this.markerBPopover);
    }
    getCellCss(isFullRange) {
        if (this.isDisabled) {
            return TABLE_CELL_DISABLED;
        }
        if (this.isSunday() && this._inRange) {
            return isFullRange ? TABLE_CELL_RANGE_BOUND : SUNDAY_RANGE;
        }
        if (isFullRange && this._inRange) {
            return TABLE_CELL_RANGE_BOUND;
        }
        if (this.isSelected) {
            return isFullRange ? TABLE_CELL_RANGE_BOUND : TABLE_CELL_SELECTED;
        }
        return `${this.isToday && !this._inRange && this._isTodayHighlighted
            ? TABLE_CELL_TODAY
            : ''}`;
    }
    setSelected() {
        if (this.isSelected && this.element && !this.isDisabled) {
            this.element.classList.add(TABLE_CELL_SELECTED);
        }
    }
}

const { SETTINGS_CONTAINER } = BxfCalendarConstants.STRING;
class BxfOverlayCalendarSettingsController extends BxfOverlayDropdownController {
    host;
    parentElement;
    targetElement;
    sourceElement;
    updateStateCallback;
    options;
    isOutsideOverlayCallback;
    constructor(host, parentElement, targetElement, sourceElement, updateStateCallback, options = {}, isOutsideOverlayCallback) {
        super(host, parentElement, targetElement, sourceElement, updateStateCallback, options, isOutsideOverlayCallback);
        this.host = host;
        this.parentElement = parentElement;
        this.targetElement = targetElement;
        this.sourceElement = sourceElement;
        this.updateStateCallback = updateStateCallback;
        this.options = options;
        this.isOutsideOverlayCallback = isOutsideOverlayCallback;
    }
    /**
     * Override focus trap context to stay within calendar container.
     *
     * @readonly
     */
    get focusTrapContext() {
        return querySelectorDeep(`.${SETTINGS_CONTAINER}`, this.sourceElement);
    }
    /**
     * Get item to focus when overlay opened.
     * Overrides listbox set in BxfOverlayDropdownController
     * @see BxfOverlayDropdownController
     */
    getItemToFocus() {
        return this.focusTrapContext;
    }
}

class BxfCalendarSettingsConstants {
    static STRING = {
        DEFAULT_DAY: 15,
        DEFAULT_MONTH: 0,
    };
}

export { BxfCalendarCell as B, BxfCalendarSettingsConstants as a, BxfOverlayCalendarSettingsController as b };
